import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { configService } from 'api/services/configService';
import ConfigListEditor from 'components/modules/config-module/ConfigListEditor';
import type { ConfigListItem } from 'api/types/config.types';

/**
 * Term types (settings cleanup C9, 2026-10-27): the kinds of terms a property can hold (booking
 * confirmation, cancellation, check-in policy…). Maintained here, in the Property Terms master that
 * uses them, rather than in Settings. Stored as the system-wide `term_types` list (PROPERTY_TERMS
 * group); a type is never deleted, only made inactive, so existing terms keep theirs.
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** After a save, so the screen's type picker picks up the change. */
  onChanged: () => void;
}

const TERM_TYPES_GROUP = 'PROPERTY_TERMS';
const TERM_TYPES_KEY = 'term_types';

const TermTypesModal: React.FC<Props> = ({ isOpen, onClose, onChanged }) => {
  const [items, setItems] = useState<ConfigListItem[]>([]);
  const [original, setOriginal] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    configService.getEffectiveConfig(TERM_TYPES_GROUP)
      .then((res) => {
        if (cancelled) return;
        const list = (res.data ?? []).find((v) => v.configKey === TERM_TYPES_KEY)?.listValues ?? [];
        setItems(list);
        setOriginal(new Set(list.map((i) => i.value)));
      })
      .catch((err: any) => notificationService.toast.error(err?.response?.data?.message || 'The term types could not be loaded.'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const save = useCallback(async () => {
    const values = items.filter((i) => i.value.trim().length > 0);
    setSaving(true);
    try {
      await configService.updateValues({
        scopeType: 'SYSTEM',
        values: [{ configKey: TERM_TYPES_KEY, listValues: values }],
      });
      notificationService.toast.success('Term types saved.');
      onChanged();
      onClose();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The term types could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [items, onChanged, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container" style={{ minHeight: 'auto', maxWidth: 560 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Term types</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={18} /></button>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            The kinds of terms a property can hold. A type in use is never deleted: make it inactive to stop offering it.
          </div>
          {loading
            ? <div className="config-loading">Loading...</div>
            : <ConfigListEditor values={items} onChange={setItems} originalValues={original} isReadOnly={saving} />}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

export default TermTypesModal;
